import { Badge, Button, Group, Paper, Stack, Text } from "@mantine/core"
import { useEffect, useState } from "react"
import { IconArrowLeft, IconMail, IconUsers, appIcon } from "atlas_forecasting_app/components/common/icons"
import { CenteredMessage, EmptyState, ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"

const FILTER_OPTIONS = [
  { value: "all", label: "All messages" },
  { value: "unreviewed", label: "Unreviewed only" },
]

function reviewBadgeColor(contactMessage) {
  return contactMessage.reviewed_at ? "teal" : "yellow"
}

function emptyMessage(filter) {
  return filter === "unreviewed" ? "No contact messages are awaiting review." : "No contact messages found."
}

export function AdminContactMessagesPage({ currentUser }) {
  const isAdmin = !!currentUser?.roles?.includes("admin")
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [filter, setFilter] = useState("all")
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [updatingId, setUpdatingId] = useState(null)
  const [error, setError] = useState("")

  const loadMessages = async ({ reset = false } = {}) => {
    if (!isAdmin) return
    if (reset) {
      setLoading(true)
    } else {
      if (!cursor || loadingMore) return
      setLoadingMore(true)
    }

    try {
      const qs = new URLSearchParams()
      qs.set("limit", "25")
      qs.set("filter", filter)
      if (!reset && cursor) qs.set("cursor", cursor)

      const data = await apiFetch(`/api/admin/contact_messages?${qs.toString()}`)
      const contactMessages = data.contact_messages || []
      setItems((prev) => (reset ? contactMessages : prev.concat(contactMessages)))
      setCursor(data.next_cursor || null)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    setItems([])
    setCursor(null)
    setError("")
    loadMessages({ reset: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isAdmin])

  const setReviewed = async (contactMessageId, reviewed) => {
    if (updatingId) return
    setUpdatingId(contactMessageId)

    try {
      const data = await apiFetch(`/api/admin/contact_messages/${contactMessageId}/review`, {
        method: "PATCH",
        body: { reviewed },
      })
      const updatedContactMessage = data?.contact_message
      if (!updatedContactMessage) return

      setItems((prev) => (
        filter === "unreviewed" && reviewed
          ? prev.filter((contactMessage) => contactMessage.id !== contactMessageId)
          : prev.map((contactMessage) => (contactMessage.id === contactMessageId ? updatedContactMessage : contactMessage))
      ))
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setUpdatingId(null)
    }
  }

  if (!currentUser) {
    return <CenteredMessage title="Admin" body="Log in required." />
  }

  if (!isAdmin) {
    return <CenteredMessage title="Admin" body="You don’t have access to this page." />
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Contact messages"
        subtitle="Review inbound messages from public and signed-in users."
        action={
          <Button component={RouterLink} to="/admin" variant="light" leftSection={appIcon(IconArrowLeft, "inline")}>
            Back to admin
          </Button>
        }
      />

      <SectionCard title="Filters" subtitle="Switch between the full inbox and messages still awaiting review.">
        <Group gap="sm">
          {FILTER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              type="button"
              variant={filter === option.value ? "filled" : "light"}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </Group>
      </SectionCard>

      <ErrorAlert message={error} />

      {loading ? <LoadingState label="Loading contact messages..." /> : null}
      {!loading && items.length === 0 && !error ? <EmptyState message={emptyMessage(filter)} /> : null}

      <Stack gap="md">
        {items.map((contactMessage) => {
          const reviewerLabel = contactMessage.reviewed_by_user?.email || (
            contactMessage.reviewed_by_user?.id != null ? `User #${contactMessage.reviewed_by_user.id}` : "another admin"
          )

          return (
            <Paper key={contactMessage.id} withBorder radius="md" shadow="xs" p="lg">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start" gap="md">
                  <Stack gap={4}>
                    <Text fw={600}>{contactMessage.subject || `Message #${contactMessage.id}`}</Text>
                    <Text size="sm" c="dimmed">
                      Message #{contactMessage.id}
                      {contactMessage.created_at ? ` • received ${formatDateTime(contactMessage.created_at)}` : ""}
                    </Text>
                  </Stack>

                  <Group gap="xs">
                    <Badge variant="light" leftSection={appIcon(IconMail, "inline")}>
                      {contactMessage.email}
                    </Badge>
                    <Badge variant="light" color={reviewBadgeColor(contactMessage)}>
                      {contactMessage.reviewed_at ? "Reviewed" : "Needs review"}
                    </Badge>
                  </Group>
                </Group>

                <Paper withBorder radius="sm" p="md">
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>
                      Message
                    </Text>
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {contactMessage.message}
                    </Text>
                  </Stack>
                </Paper>

                {contactMessage.user ? (
                  <Paper withBorder radius="sm" bg="blue.0" p="md">
                    <Stack gap="xs">
                      <Text size="sm" fw={600} c="blue.9">
                        Linked Atlas user
                      </Text>
                      <Text size="sm" c="blue.9">
                        {contactMessage.user.email || `User #${contactMessage.user.id}`}
                      </Text>
                    </Stack>
                  </Paper>
                ) : null}

                {contactMessage.reviewed_at ? (
                  <Text size="sm" c="dimmed">
                    Reviewed by {reviewerLabel} on {formatDateTime(contactMessage.reviewed_at)}.
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed">
                    Mark this message as reviewed once it has been triaged.
                  </Text>
                )}

                <Group justify="space-between" align="center" gap="md">
                  <Group gap="xs">
                    {contactMessage.user ? (
                      <Button component={RouterLink} to={`/admin/users/${contactMessage.user.id}`} variant="light" leftSection={appIcon(IconUsers, "inline")}>
                        Open user
                      </Button>
                    ) : null}
                  </Group>

                  <Button
                    type="button"
                    color={contactMessage.reviewed_at ? "yellow" : "teal"}
                    variant="light"
                    disabled={updatingId === contactMessage.id}
                    onClick={() => setReviewed(contactMessage.id, !contactMessage.reviewed_at)}
                  >
                    {updatingId === contactMessage.id
                      ? "Saving..."
                      : (contactMessage.reviewed_at ? "Mark unreviewed" : "Mark reviewed")}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )
        })}

        {cursor ? (
          <Group justify="center">
            <Button type="button" variant="light" disabled={loadingMore} onClick={() => loadMessages()}>
              {loadingMore ? "Loading..." : "Load more"}
            </Button>
          </Group>
        ) : null}
      </Stack>
    </Stack>
  )
}
